import { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { updatePatientsFromId } from '../../../../redux/actions/patientsActions';
import { IPatient, ReduxStore } from '../../../../constants/types';

/**
 * The analytics tabs' view of the decrypted patient store, and the one lifecycle that fills it.
 *
 * SHER-897 extracted this. Two tabs needed the same three things — the selector, a record of
 * whether the bulk decrypt has finished, and the dispatch that starts it — and each was about to
 * carry its own copy, along with its own comment block re-arguing the same invariants. There is
 * a third, older variant in `useInpatientsData` using a ref; it has no `settled` consumer, so it
 * is left alone rather than dragged into this change.
 *
 * `settled` is not `patients.length > 0`. It says the decrypt has FINISHED, whatever it
 * produced, which is the only thing that separates "not here yet" from "not coming" — and
 * without that separation a failed decrypt parks a card on a skeleton for the life of the mount.
 *
 * A failure is terminal for the life of the mount, deliberately. Re-arming looks like a free
 * retry and is not: this is a paginated bulk download, the status is an effect dependency, and
 * on React 17 a state write inside a promise callback is not batched — so an effect that
 * re-armed itself on its own settle would re-issue the download as fast as it can round-trip
 * against an endpoint that is already failing. A real retry belongs on a control the user
 * presses, which is a product decision.
 */
export const useDecryptedPatients = (
  investigationUuid: string | undefined,
  enabled: boolean,
): { patients: IPatient[]; settled: boolean } => {
  const dispatch = useDispatch();
  const [status, setStatus] = useState<'idle' | 'loading' | 'settled'>('idle');

  // Deliberately keyed on the CURRENT investigation in the store rather than on
  // `investigationUuid`: that parameter feeds the dispatch, and switching the selector to it
  // would quietly change which investigation's patients are read.
  const patients = useSelector((state: ReduxStore) => {
    const currentInvestigation = state.investigations.currentInvestigation;
    if (!currentInvestigation) return [];
    return state.patients.data?.[currentInvestigation.uuid] as IPatient[] || [];
  }, shallowEqual);

  useEffect(() => {
    if (enabled && !patients.length && status === 'idle' && investigationUuid) {
      setStatus('loading');
      // One handler for both outcomes rather than a `.catch`/`.finally` pair over the same
      // variable: `finally` runs after `catch`, so a pair lets the two disagree. Settling is
      // also the only signal both failure modes share — the offline path resolves without
      // throwing and still writes nothing.
      const settle = () => setStatus('settled');
      Promise.resolve(dispatch(updatePatientsFromId(investigationUuid, true))).then(settle, settle);
    }
  }, [enabled, investigationUuid, patients.length, status, dispatch]);

  return { patients, settled: status === 'settled' };
};
